import React, { FC, useRef } from 'react';
import Carousel, { CarouselInternalState } from 'react-multi-carousel';
import { TrimCarouselProps } from '.';
import TrimItem from '../TrimItem';
import { ITrimItem } from '../../redux/trimSelector/trimSelector.duck.interface';
import IconButton from '../IconButton';
import { Icons } from '../Icon';
import { isBrandDS } from 'src/constants/main';
import styled from 'styled-components';
import { breakpoints, css } from '../../styles/theme';
import media from '../../styles/media';
import Equalizer from 'react-equalizer';
import { sortOffersDescByPriority } from '@utils/Offers.utils';
import useTranslations from '@hooks/useTranslations';
import StockCard from '../StockModelCard';
import { PaymentJourneyTypes } from '../../partExchange/interfaces/Default';
import { trimCarouselBreakpoints } from '../../styles/trimCarouselBreakpoints';
import { EMPLOYEE_PRICE_TYPE } from '../../services/filters/filters';
import { hasMonthlyPriceByType } from '@utils/Price.utils';
import { EContextPage, EType, findPromoByPageAndType } from '@utils/Promo.utils';
import { IOffer } from '../../interfaces/IOfferItem';
import { ROLES } from 'src/constants/main';
import { useContainerHeightNormalize } from '@hooks/useContainerNormalize';

const StyledCarousel = styled(Carousel)<{ isStockSliceCarousel: boolean }>`
    ${media.lg`
        position: unset;
    `}

    ${(props) =>
        props.isStockSliceCarousel &&
        css`
            max-width: unset !important;

            .trimItemWrap {
                & > div {
                    justify-content: center;
                    min-width: 95%;
                    & > div {
                        ${!isBrandDS &&
                        css`
                            grid-template-rows: auto 1fr 3rem auto 3rem 1fr;
                        `}
                        height: 100%;
                        @media (max-width: ${breakpoints.xs}px) {
                            margin: auto !important;
                        }
                    }
                }
            }

            li {
                @media (max-width: ${breakpoints.xs}px) {
                    padding: unset !important;
                }
            }
        `}
`;

const responsive = (isStockSliceCarousel: boolean) => {
    if (isStockSliceCarousel) {
        return {
            desktop: {
                breakpoint: { max: 3000, min: breakpoints.lg },
                items: 3,
            },
            tablet: {
                breakpoint: { max: trimCarouselBreakpoints.lg, min: breakpoints.sm },
                items: 2,
            },
            mobile: {
                breakpoint: { max: trimCarouselBreakpoints.md, min: 0 },
                items: 1,
            },
        };
    }

    return {
        desktop: {
            breakpoint: { max: 3000, min: breakpoints.lg },
            items: 3,
        },
        tablet: {
            breakpoint: { max: breakpoints.lg, min: breakpoints.sm },
            items: 2,
        },
        mobile: {
            breakpoint: { max: breakpoints.sm, min: 0 },
            items: 1,
        },
    };
};

interface ICustomButtonGroupAsArrows {
    next?(): void;
    previous?(): void;
    carouselState?: CarouselInternalState;
    totalTrims: number;
    filteredTrims: ITrimItem[];
}

const CustomButtonGroupAsArrows = ({
    next,
    previous,
    totalTrims,
    filteredTrims,
    ...rest
}: ICustomButtonGroupAsArrows) => {
    const {
        carouselState: { currentSlide, deviceType },
    } = rest;

    enum CarouselArrowButtonType {
        PREV = 'prev',
        NEXT = 'next',
    }

    const { t } = useTranslations();

    const getCssClasses = (button: CarouselArrowButtonType) => {
        const cssClasses: string[] = ['arrowBtn'];
        switch (button) {
            // left arrow
            case CarouselArrowButtonType.PREV:
                cssClasses.push('arrowBtn--left');
                if (currentSlide === 0) {
                    cssClasses.push('arrowBtn--disabled');
                }
                break;
            // right arrow
            case CarouselArrowButtonType.NEXT:
                if (
                    (deviceType === 'mobile' && currentSlide >= totalTrims - 1) ||
                    (deviceType === 'tablet' && currentSlide >= totalTrims - 2) ||
                    (deviceType === 'desktop' && currentSlide >= totalTrims - 3)
                ) {
                    cssClasses.push('arrowBtn--disabled');
                }
                break;
        }
        return cssClasses.join(' ');
    };

    return (
        <div className={`carouselArrows-wrapper ${filteredTrims.length <= 3 && 'trimCarouselCenter'}`}>
            <div className="carouselArrows-wrapper__btnsSection">
                <div
                    className={`arrow-icon-holder arrow-icon-holder-left ${
                        getCssClasses(CarouselArrowButtonType.PREV).includes('arrowBtn--disabled') &&
                        'arrow-icon-holder-left-hide'
                    }`}
                >
                    <IconButton
                        className={getCssClasses(CarouselArrowButtonType.PREV)}
                        icon={Icons.ChevronRightThin}
                        onClick={previous}
                        iconClass="iconClass"
                        dataTestId="TESTING_PREVIOUS_BTN"
                        altId="svgImgCarouselPrev"
                        altText={t('trims.counterWithoutCurrent', { total: filteredTrims.length })}
                        role={ROLES.IMG}
                    />
                </div>
                <div className="currentSlideSection">
                    {t('trims.counter', { current: currentSlide + 1, total: totalTrims })}
                </div>
                <div
                    className={`arrow-icon-holder arrow-icon-holder-right ${
                        getCssClasses(CarouselArrowButtonType.NEXT).includes('arrowBtn--disabled') &&
                        'arrow-icon-holder-right-hide'
                    }`}
                >
                    <IconButton
                        className={getCssClasses(CarouselArrowButtonType.NEXT)}
                        iconClass="iconClass"
                        icon={Icons.ChevronRightThin}
                        onClick={next}
                        dataTestId="TESTING_NEXT_BTN"
                        altId="svgImgCarouselNext"
                        altText={t('trims.counterWithoutCurrent', { total: filteredTrims.length })}
                        role={ROLES.IMG}
                    />
                </div>
            </div>
        </div>
    );
};

export const TrimCarouselTemplate: FC<TrimCarouselProps> = ({
    className,
    filteredTrims,
    filteredOutTrims,
    trimConfigurations,
    usingFilters,
    disabled,
    isStockSliceCarousel = false,
    normalizeEnabled = true,
}) => {
    const priceBoxRef = useRef();
    const priceBoxContainerRef = useRef();
    const priceBoxCatalogRef = useRef();
    const headerRef = useRef();
    const equipmentSubtitleContainerRef = useRef();
    const descriptionWrapperRef = useRef();
    const imageRef = useRef();
    const financeLabelContainerRef = useRef();
    const benefitsRef = useRef();
    const equipmentLisRef = useRef();

    const nodesToEqualize: [] = [];

    const addUniqueNode = (node: any) => {
        //@ts-ignore
        if (node && nodesToEqualize.indexOf(node) === -1) {
            //@ts-ignore
            nodesToEqualize.push(node);
        }
    };

    const sortedTrims = isStockSliceCarousel ? filteredTrims : filteredTrims.sort(sortOffersDescByPriority);

    const sortedFilteredOutTrims = (
        isStockSliceCarousel ? filteredOutTrims : filteredOutTrims.sort(sortOffersDescByPriority)
    ).map((item) => ({ ...item, isDisabled: true }));
    const totalItems = sortedTrims.length + sortedFilteredOutTrims.length;

    //filter all trims to see if there is any with benefits, than we can apply special class
    const sortedTrimsWithBenefits = sortedTrims
        .map((trim) => trim.offers?.filter((offer) => offer.benefits).length > 0)
        .filter((trimWithBenefits) => trimWithBenefits === true);

    const sortedTrimsWithMonthlyPrices = sortedTrims.filter((trim) =>
        hasMonthlyPriceByType(trim.prices, EMPLOYEE_PRICE_TYPE)
    );

    const trimItems: ITrimItem[] = [...sortedTrims, ...sortedFilteredOutTrims].map((item, index) => ({
        ...item,
        index,
    }));

    useContainerHeightNormalize({
        containers: {
            headerRef: { selector: '.headerRef', element: headerRef },
            equipmentSubtitleContainerRef: {
                selector: '.equipmentSubtitleContainerRef',
                element: equipmentSubtitleContainerRef,
            },
            descriptionWrapperRef: { selector: '.descriptionWrapperRef', element: descriptionWrapperRef },
            priceBoxCatalogRef: { selector: '.priceBoxCatalogRef', element: priceBoxCatalogRef },
            imageRef: { selector: '.imageRef', element: imageRef },
            carImageWrap: { selector: '', element: null },
            financeLabelContainerRef: { selector: '.financeLabelContainerRef', element: financeLabelContainerRef },
            benefitsRef: { selector: '.benefitsRef', indent: 10, element: benefitsRef },
            equipmentListRef: { selector: '.equipmentListRef', indent: 20, element: null },
            priceBoxWrapperRef: { selector: '.priceBoxWrapperRef', indent: 10, element: null },
        },
        enabled: normalizeEnabled,
        dataSet: trimItems,
        mainClassName: 'priceBoxWrapper',
    });

    const isMarketingStickerExists = Boolean(
        trimItems
            .map((item) => !!findPromoByPageAndType(item.offers as (IOffer & { type: EType })[], EContextPage.trim))
            .filter(Boolean).length
    );

    return (
        <div className={className}>
            <StyledCarousel
                responsive={responsive(isStockSliceCarousel)}
                ssr={true}
                showDots={false}
                slidesToSlide={1}
                keyBoardControl={true}
                containerClass="trimCarousel"
                itemClass="image-item"
                arrows={false}
                customButtonGroup={<CustomButtonGroupAsArrows filteredTrims={trimItems} totalTrims={totalItems} />}
                key={totalItems}
                renderButtonGroupOutside
                isStockSliceCarousel={isStockSliceCarousel}
            >
                {trimItems.map((trim: ITrimItem, i: number) => (
                    <Equalizer
                        className={`trimItemWrap${sortedTrimsWithBenefits.length ? ' benefits' : ''}${
                            sortedTrimsWithMonthlyPrices.length ? ' monthlyPrices' : ''
                        }`}
                        byRow={false}
                        nodes={() => nodesToEqualize}
                        key={i}
                    >
                        {isStockSliceCarousel ? (
                            <StockCard
                                vehicle={trim as any}
                                paymentJourney={PaymentJourneyTypes.CASH}
                                isStockSlice={isStockSliceCarousel}
                                index={i}
                            />
                        ) : (
                            <TrimItem
                                index={i}
                                isMarketingStickerExists={isMarketingStickerExists}
                                newRef={addUniqueNode}
                                trim={trim}
                                trimConfiguration={trimConfigurations[trim.specPackSlug]}
                                usingFilters={usingFilters}
                                disabled={disabled || trim.isDisabled}
                                childRef={{
                                    priceBoxRef,
                                    priceBoxContainerRef,
                                    priceBoxCatalogRef,
                                    headerRef,
                                    equipmentSubtitleContainerRef,
                                    descriptionWrapperRef,
                                    imageRef,
                                    financeLabelContainerRef,
                                    benefitsRef,
                                    equipmentLisRef,
                                }}
                            />
                        )}
                    </Equalizer>
                ))}
            </StyledCarousel>
        </div>
    );
};
